export interface EmojiInfo {
  char: string;
  codepoints: string[];       // hex, upper-case, at least 4 digits
  unicode: string;            // U+1F600 U+200D ...
  htmlDecimal: string;
  htmlHex: string;
  js: string;                 // \u{1F600}
  jsSurrogates: string;       // 😀
  css: string;                // \1F600 (CSS content escapes, space-terminated)
  python: string;             // \U0001F600
  utf8: string;               // F0 9F 98 80
  hexcode: string;            // 1F468-200D-1F469 (matches emojibase)
}

const hex = (n: number) => n.toString(16).toUpperCase();
const pad = (s: string, n: number) => s.padStart(n, "0");

export function describeEmoji(char: string): EmojiInfo {
  const cps = Array.from(char).map((c) => c.codePointAt(0)!);
  const hexes = cps.map((c) => pad(hex(c), 4));
  const bytes = Array.from(new TextEncoder().encode(char), (b) => pad(hex(b), 2)).join(" ");
  return {
    char,
    codepoints: hexes,
    unicode: hexes.map((h) => `U+${h}`).join(" "),
    htmlDecimal: cps.map((c) => `&#${c};`).join(""),
    htmlHex: hexes.map((h) => `&#x${h};`).join(""),
    js: hexes.map((h) => (h.length > 4 ? `\\u{${h}}` : `\\u${h}`)).join(""),
    jsSurrogates: Array.from({ length: char.length }, (_, i) => `\\u${pad(hex(char.charCodeAt(i)), 4)}`).join(""),
    css: cps.map((c) => `\\${hex(c)} `).join("").trimEnd(),
    python: cps.map((c) => (c > 0xffff ? `\\U${pad(hex(c), 8)}` : `\\u${pad(hex(c), 4)}`)).join(""),
    utf8: bytes,
    hexcode: hexes.join("-"),
  };
}

/** Splits text into user-perceived characters, so 👨‍👩‍👧, 🇺🇸, 👋🏽 and 1️⃣ each stay in one piece. */
export function graphemes(text: string): string[] {
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: string }) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
  if (Seg) return Array.from(new Seg(undefined, { granularity: "grapheme" }).segment(text), (s) => s.segment);
  return Array.from(text);
}

export function describeText(text: string): EmojiInfo[] {
  return graphemes(text).filter((g) => !/^[\r\n]+$/.test(g)).map(describeEmoji);
}

const ASCII_PUNCT: Record<string, string> = {" ": "SPACE", "!": "EXCLAMATION MARK", "\"": "QUOTATION MARK", "#": "NUMBER SIGN", "$": "DOLLAR SIGN", "%": "PERCENT SIGN", "&": "AMPERSAND", "'": "APOSTROPHE", "(": "LEFT PARENTHESIS", ")": "RIGHT PARENTHESIS", "*": "ASTERISK", "+": "PLUS SIGN", ",": "COMMA", "-": "HYPHEN-MINUS", ".": "FULL STOP", "/": "SOLIDUS", ":": "COLON", ";": "SEMICOLON", "<": "LESS-THAN SIGN", "=": "EQUALS SIGN", ">": "GREATER-THAN SIGN", "?": "QUESTION MARK", "@": "COMMERCIAL AT", "[": "LEFT SQUARE BRACKET", "\\": "REVERSE SOLIDUS", "]": "RIGHT SQUARE BRACKET", "^": "CIRCUMFLEX ACCENT", "_": "LOW LINE", "`": "GRAVE ACCENT", "{": "LEFT CURLY BRACKET", "|": "VERTICAL LINE", "}": "RIGHT CURLY BRACKET", "~": "TILDE"};
const DIGITS = ["ZERO", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE"];

/** Official Unicode name of a printable ASCII character (U+0020 to U+007E), else "". Emoji names come from emojibase. */
export function asciiName(char: string): string {
  if (char.length !== 1) return "";
  if (/[A-Z]/.test(char)) return `Latin Capital Letter ${char}`;
  if (/[a-z]/.test(char)) return `Latin Small Letter ${char.toUpperCase()}`;
  if (/[0-9]/.test(char)) return `Digit ${DIGITS[Number(char)].toLowerCase()}`;
  return (ASCII_PUNCT[char] ?? "").toLowerCase();
}

/** CSS hex escapes end at a space or after 6 digits: a following 0-9/A-F letter would be swallowed without one. */
export const CSS_ESCAPE_NOTE = "CSS escapes end at a space. Keep the space if the next character is 0-9 or A-F, or write the code with 6 digits (\\000041).";
